import React from 'react';
import { loginWithToken, isSessionValid, clearAuthData, getDecodedToken, isTokenExpired } from '@/app/utils/auth';
import { DecodedToken } from '@/shared/types/auth';

export interface AuthContext {
  /** Whether the user is authenticated with a valid session */
  isAuthenticated: boolean;
  /** Decoded JWT token */
  decodedToken: DecodedToken | null;
  /** Session identifier */
  sessionIdentifier: string | null;
  /** Login with JWT access token */
  loginWithToken: (accessToken: string) => Promise<void>;
  /** Logout and clear all auth data */
  logout: () => Promise<void>;
  /** Check if the current session is still valid */
  validateSession: () => Promise<boolean>;
  /** Loading state for auth operations */
  isLoading: boolean;
  /** Error state for auth operations */
  error: string | null;
  /** Clear error state */
  clearError: () => void;
}

const AuthContext = React.createContext<AuthContext | null>(null);

const SESSION_CHECK_INTERVAL = 5 * 60 * 1000; // Check session validity every 5 minutes

/**
 * Get stored session identifier from localStorage
 */
function getStoredSessionIdentifier(): string | null {
  return localStorage.getItem('sessionIdentifier');
}

export function AuthProvider({ children }: { children: React.ReactNode }) {
  const [decodedToken, setDecodedToken] = React.useState<DecodedToken | null>(getDecodedToken());
  const [sessionIdentifier, setSessionIdentifier] = React.useState<string | null>(getStoredSessionIdentifier());
  const [isLoading, setIsLoading] = React.useState(true);
  const [error, setError] = React.useState<string | null>(null);
  const sessionCheckIntervalRef = React.useRef<NodeJS.Timeout | null>(null);

  /**
   * Logout and clear all authentication data
   * Stable function using setState callbacks to avoid dependencies
   */
  const logout = React.useCallback(async () => {
    try {
      setIsLoading(true);
      
      // Clear interval
      if (sessionCheckIntervalRef.current) {
        clearInterval(sessionCheckIntervalRef.current);
        sessionCheckIntervalRef.current = null;
      }

      // Clear all auth data
      clearAuthData();
      
      // Clear state using functional updates (no dependencies on current state)
      setDecodedToken(null);
      setSessionIdentifier(null);
      setError(null);
      
    } catch (err) {
      console.error('Logout error:', err);
    } finally {
      setIsLoading(false);
    }
  }, []); // ✅ No dependencies needed - uses refs and functional updates

  /**
   * Validate the current session
   */
  const validateSession = React.useCallback(async (): Promise<boolean> => {
    try {
      const token = getDecodedToken();
      
      if (!token) {
        await logout();
        return false;
      }

      // Check local expiration first (optimization)
      if (isTokenExpired(token)) {
        await logout();
        return false;
      }

      // Validate with backend
      const isValid = await isSessionValid();
      
      if (!isValid) {
        await logout();
        return false;
      }

      return true;
    } catch (err) {
      console.error('Session validation error:', err);
      await logout();
      return false;
    }
  }, [logout]); // ✅ Now properly includes logout

  /**
   * Login with JWT access token
   */
  const handleLoginWithToken = React.useCallback(async (accessToken: string) => {
    try {
      setIsLoading(true);
      setError(null);

      const authData = await loginWithToken(accessToken);
      
      setDecodedToken(authData.decodedToken);
      setSessionIdentifier(authData.sessionIdentifier);
      
    } catch (err) {
      const errorMessage = err instanceof Error ? err.message : 'Authentication failed';
      setError(errorMessage);
      console.error('Login error:', err);
      
      // Clear auth state on error
      setDecodedToken(null);
      setSessionIdentifier(null);
      
      throw err;
    } finally {
      setIsLoading(false);
    }
  }, []); // ✅ No dependencies - uses functional updates

  /**
   * Clear error state
   */
  const clearError = React.useCallback(() => {
    setError(null);
  }, []);

  /**
   * Initialize auth state on mount
   */
  React.useEffect(() => {
    const initializeAuth = async () => {
      try {
        setIsLoading(true);
        
        const storedToken = getDecodedToken();
        const storedSessionId = getStoredSessionIdentifier();

        if (storedToken && storedSessionId) {
          // Validate the stored session
          const isValid = await isSessionValid();
          
          if (isValid) {
            setDecodedToken(storedToken);
            setSessionIdentifier(storedSessionId);
          } else {
            // Session is invalid, clear everything
            await logout();
          }
        }
      } catch (err) {
        console.error('Auth initialization error:', err);
        await logout();
      } finally {
        setIsLoading(false);
      }
    };

    void initializeAuth();
  }, [logout]); // ✅ Now includes logout dependency

  /**
   * Set up periodic session validation
   */
  React.useEffect(() => {
    if (sessionIdentifier) {
      // Clear any existing interval
      if (sessionCheckIntervalRef.current) {
        clearInterval(sessionCheckIntervalRef.current);
      }

      // Set up new interval for session validation
      sessionCheckIntervalRef.current = setInterval(() => {
        void validateSession();
      }, SESSION_CHECK_INTERVAL);
    }

    return () => {
      if (sessionCheckIntervalRef.current) {
        clearInterval(sessionCheckIntervalRef.current);
        sessionCheckIntervalRef.current = null;
      }
    };
  }, [sessionIdentifier, validateSession]); // ✅ All dependencies included

  const isAuthenticated = !!sessionIdentifier && !!decodedToken;

  const value: AuthContext = {
    isAuthenticated,
    decodedToken,
    sessionIdentifier,
    loginWithToken: handleLoginWithToken,
    logout,
    validateSession,
    isLoading,
    error,
    clearError,
  };

  return (
    <AuthContext.Provider value={value}>
      {children}
    </AuthContext.Provider>
  );
}

/**
 * Hook to access auth context
 */
export function useAuth(): AuthContext {
  const context = React.useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
}

/**
 * Hook to require authentication - redirects to auth route if not authenticated
 * Use this in protected route components
 */
export function useRequireAuth(): {
  isAuthenticated: boolean;
  isLoading: boolean;
} {
  const { isAuthenticated, isLoading, validateSession } = useAuth();

  React.useEffect(() => {
    if (!isLoading && !isAuthenticated) {
      // User is not authenticated, they will be redirected by route protection
      return;
    }

    // Validate session on mount
    if (isAuthenticated) {
      void validateSession();
    }
  }, [isAuthenticated, isLoading, validateSession]); // ✅ All dependencies included

  return { isAuthenticated, isLoading };
}